import React from 'react';
import { motion } from 'framer-motion';
import { 
  SunIcon, 
  MoonIcon, 
  ComputerDesktopIcon 
} from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from './Button';

interface ThemeToggleProps {
  variant?: 'button' | 'dropdown' | 'switch';
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'button',
  showLabel = false,
  size = 'md',
  className = '',
}) => {
  const { mode, setMode, toggleMode, isDarkMode } = useTheme();

  const getIcon = () => {
    switch (mode) {
      case 'light':
        return <SunIcon className="w-5 h-5" />;
      case 'dark':
        return <MoonIcon className="w-5 h-5" />;
      case 'system':
        return <ComputerDesktopIcon className="w-5 h-5" />;
      default:
        return <SunIcon className="w-5 h-5" />;
    }
  };

  const getLabel = () => {
    switch (mode) {
      case 'light':
        return 'Light Mode';
      case 'dark':
        return 'Dark Mode';
      case 'system':
        return 'System';
      default:
        return 'Light Mode';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-1 text-sm';
      case 'md':
        return 'px-3 py-2';
      case 'lg':
        return 'px-4 py-3 text-lg';
      default:
        return 'px-3 py-2';
    }
  };

  if (variant === 'switch') {
    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        {showLabel && (
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {getLabel()}
          </span>
        )}
        <motion.button
          onClick={toggleMode}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full
            ${isDarkMode ? 'bg-primary-600' : 'bg-gray-200'}
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
            transition-colors duration-200 ease-in-out
            ${getSizeClasses()}
          `}
          whileTap={{ scale: 0.95 }}
        >
          <motion.span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white shadow-lg
              ring-0 transition duration-200 ease-in-out
            `}
            animate={{
              x: isDarkMode ? 24 : 4,
            }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        </motion.button>
      </div>
    );
  }

  if (variant === 'dropdown') {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setMode('light')}
            className={`
              p-2 rounded-md transition-colors duration-200
              ${mode === 'light' 
                ? 'bg-primary-100 text-primary-700' 
                : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              }
            `}
            title="Light mode"
          >
            <SunIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setMode('dark')}
            className={`
              p-2 rounded-md transition-colors duration-200
              ${mode === 'dark' 
                ? 'bg-primary-100 text-primary-700' 
                : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              }
            `}
            title="Dark mode"
          >
            <MoonIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setMode('system')}
            className={`
              p-2 rounded-md transition-colors duration-200
              ${mode === 'system' 
                ? 'bg-primary-100 text-primary-700' 
                : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              }
            `}
            title="System mode"
          >
            <ComputerDesktopIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Default button variant
  return (
    <Button
      variant="ghost"
      size={size}
      onClick={toggleMode}
      className={`
        relative overflow-hidden
        ${getSizeClasses()}
        ${className}
      `}
      title={`Switch to ${mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light'} mode`}
    >
      <motion.div
        key={mode}
        initial={{ opacity: 0, rotate: -180 }}
        animate={{ opacity: 1, rotate: 0 }}
        exit={{ opacity: 0, rotate: 180 }}
        transition={{ duration: 0.3 }}
        className="flex items-center space-x-2"
      >
        {getIcon()}
        {showLabel && (
          <span className="text-sm font-medium">
            {getLabel()}
          </span>
        )}
      </motion.div>
    </Button>
  );
};

export default ThemeToggle;
